import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";

// Standard Allison staff-briefing presentation. Presentation-only: renders the
// EXISTING internal summary text as a structured case memorandum. No AI calls.

const GROUPS = [
  { id: "overview", title: "Client Overview", icon: "User", match: ["CLIENT", "OVERVIEW", "BACKGROUND", "PROFILE", "MARITAL", "FAMILY", "DEPENDENT"] },
  { id: "income", title: "Income Profile", icon: "Wallet", match: ["INCOME", "PENSION", "SALARY", "EARNINGS", "REVENUE", "WAGES", "DISTRIBUTION"] },
  { id: "residency", title: "Residency & Immigration", icon: "Plane", match: ["VISA", "RESIDENC", "IMMIGRATION", "TIN", "DOMICILE", "CITIZEN", "PERMIT"] },
  { id: "filing", title: "Filing History", icon: "FileText", match: ["FILING", "RETURN", "PAYE", "WITHHOLD", "PND", "LODGE", "PRIOR YEAR"] },
  { id: "tax", title: "Tax Situation", icon: "Scale", match: ["TAX SITUATION", "EXEMPTION", "REMITTANCE", "ASSESSAB", "LIABILITY", "TAXABLE", "DEDUCT", "RULE"] },
  { id: "compliance", title: "Compliance Risks", icon: "AlertTriangle", match: ["COMPLIANCE", "RISK", "OVERDUE", "PENALTY", "DEADLINE", "EXPOSURE", "ISSUE", "CONCERN"] },
  { id: "treaty", title: "Treaty / International Considerations", icon: "Globe", match: ["DTA", "TREATY", "HMRC", "FOREIGN TAX", "CREDIT", "CROSS-BORDER", "INTERNATIONAL", "NT CODE", "COMPETENT", "CERTIFICATE"] },
  { id: "recs", title: "Allison's Recommendations", icon: "Target", match: ["RECOMMEND", "NEXT STEP", "DELIVERABLE", "ACTION", "STRATEGY", "PLAN", "PROPOSE", "KEY DELIVER"] },
];
const GROUP_TONE = {
  compliance: { head: "text-rose-700", bar: "bg-rose-400", dot: "text-rose-400", iconBg: "bg-rose-50 text-rose-600" },
  recs: { head: "text-blue-700", bar: "bg-blue-400", dot: "text-blue-500", iconBg: "bg-blue-50 text-blue-600" },
  default: { head: "text-slate-800", bar: "bg-slate-300", dot: "text-slate-300", iconBg: "bg-slate-100 text-slate-500" },
};

function classifyLabel(label) {
  const up = label.toUpperCase();
  for (const g of GROUPS) {
    if (g.match.some((k) => up.includes(k))) return g.id;
  }
  return "notes";
}

// Split the free-form summary into { label, content } segments by inline ALL-CAPS "LABEL:" markers.
function parseSegments(text) {
  if (!text) return [];
  const re = /(^|[.;\n]\s+)([A-Z][A-Z0-9 &/'’.\-]{1,44}?):\s/g;
  const hits = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const label = m[2].replace(/\s+/g, " ").trim();
    if (label.length < 3) continue;
    hits.push({ label, labelStart: m.index + m[1].length, contentStart: re.lastIndex });
  }
  if (hits.length === 0) return [];
  const segs = [];
  for (let i = 0; i < hits.length; i++) {
    const end = i + 1 < hits.length ? hits[i + 1].labelStart : text.length;
    const content = text.slice(hits[i].contentStart, end).trim().replace(/[.;\s]+$/, "");
    if (content) segs.push({ label: hits[i].label, content });
  }
  return segs;
}

function titleCase(s) {
  return s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase()).replace(/\bUk\b/g, "UK").replace(/\bUs\b/g, "US").replace(/\bLtr\b/g, "LTR").replace(/\bTin\b/g, "TIN").replace(/\bDta\b/g, "DTA").replace(/\bHmrc\b/g, "HMRC").replace(/\bPnd\b/g, "PND").replace(/\bPaye\b/g, "PAYE").replace(/\bNt\b/g, "NT");
}

// Turn a chunk of content into scannable items (safe splits only — never on commas inside numbers).
function toItems(text) {
  const t = (text || "").trim();
  if (!t) return { lead: "", items: [] };
  const numbered = /\(\s*\d+\s*\)/.test(t);
  if (numbered) {
    const parts = t.split(/\(\s*\d+\s*\)/).map((s) => s.replace(/[,;\s]+$/, "").trim()).filter(Boolean);
    if (parts.length > 1) {
      const leadIsCaption = /:$/.test(parts[0]) || parts[0].length < 60;
      return { lead: leadIsCaption ? parts[0].replace(/:$/, "") : "", items: leadIsCaption ? parts.slice(1) : parts, ordered: true };
    }
  }
  const parts = t.split(/(?<=[.;])\s+(?=[A-Z0-9£$€(])/).map((s) => s.trim()).filter(Boolean);
  return { lead: "", items: parts.length ? parts : [t] };
}

function renderInline(str) {
  // lightweight **bold** support
  const out = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) out.push(str.slice(last, m.index));
    out.push(<strong key={`b${k++}`} className="font-semibold text-slate-900">{m[1]}</strong>);
    last = re.lastIndex;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

function GroupBlock({ group, segs }) {
  const tone = GROUP_TONE[group.id] || GROUP_TONE.default;
  const Icon = Icons[group.icon] || Icons.FileText;
  const showLabels = segs.length > 1;
  return (
    <section className="mb-6" data-testid={`briefing-group-${group.id}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${tone.iconBg}`}><Icon className="h-4 w-4" /></span>
        <h4 className={`text-sm font-bold uppercase tracking-wide ${tone.head}`}>{group.title}</h4>
        <div className="flex-1 h-px bg-slate-100" />
      </div>
      <div className="pl-1 space-y-3">
        {segs.map((seg, i) => {
          const { lead, items, ordered } = toItems(seg.content);
          return (
            <div key={i}>
              {showLabels && <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{titleCase(seg.label)}</div>}
              {lead && <p className="text-sm text-slate-600 mb-1.5">{renderInline(lead)}</p>}
              {ordered ? (
                <ol className="space-y-1.5 list-none">
                  {items.map((it, j) => (
                    <li key={j} className="flex gap-2 text-sm text-slate-700 leading-relaxed">
                      <span className="shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-100 text-[11px] font-bold text-slate-500">{j + 1}</span>
                      <span>{renderInline(it)}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <ul className="space-y-1.5">
                  {items.map((it, j) => (
                    <li key={j} className="flex gap-2 text-sm text-slate-700 leading-relaxed">
                      <Icons.Dot className={`h-4 w-4 shrink-0 mt-0.5 -ml-1 ${tone.dot}`} />
                      <span>{renderInline(it)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function shortVal(v) {
  if (!v) return "";
  return String(v).split(/[(;\u2013\u2014]|—|–/)[0].trim();
}

function QuickFacts({ clientInfo, confidence, complexity, summaryText }) {
  if (!clientInfo && confidence == null && !complexity) return null;
  const ci = clientInfo || {};
  const blob = `${JSON.stringify(ci)} ${summaryText || ""}`.toLowerCase();
  let usStatus = "—";
  if (/not a u\.?s\.? person|no u\.?s\.? (citizenship|filing)/.test(blob)) usStatus = "Not a U.S. Person";
  else if (/u\.?s\.? citizen|green card|us person/.test(blob)) usStatus = "U.S. Person";
  const facts = [
    ["Nationality", shortVal(ci.citizenship)],
    ["Residence", shortVal(ci.current_country || ci.residency)],
    ["Visa", shortVal(ci.visa_status)],
    ["U.S. Status", usStatus],
    ["Confidence", confidence != null ? `${confidence}%` : ""],
    ["Complexity", complexity || ""],
  ].filter(([, v]) => v);
  if (facts.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid="briefing-quickfacts">
      {facts.map(([label, val]) => (
        <div key={label} className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
          <div className="text-sm font-semibold text-slate-800 truncate" title={val}>{val}</div>
        </div>
      ))}
    </div>
  );
}

export function AllisonBriefing({ value, onSave, clientInfo, confidence, complexity, title = "Internal Case Summary", editable = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const taRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => {
    if (editing && taRef.current) {
      const el = taRef.current;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
      el.focus();
    }
  }, [editing]);

  const grow = (e) => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; };
  const save = async () => {
    setSaving(true);
    try { await onSave?.(draft); setEditing(false); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div data-testid="briefing-edit">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-400">Editing raw summary — headings are the ALL-CAPS <span className="font-mono">LABEL:</span> markers.</span>
          <div className="flex items-center gap-2">
            <button onClick={() => { setDraft(value || ""); setEditing(false); }} data-testid="briefing-cancel" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold"><Icons.X className="h-3.5 w-3.5" />Cancel</button>
            <button onClick={save} disabled={saving} data-testid="briefing-save" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50">{saving ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Check className="h-3.5 w-3.5" />}Save</button>
          </div>
        </div>
        <textarea ref={taRef} value={draft} onChange={(e) => setDraft(e.target.value)} onInput={grow}
          data-testid="briefing-textarea"
          className="w-full min-h-[300px] overflow-hidden resize-none text-sm leading-relaxed rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
      </div>
    );
  }

  const segments = parseSegments(value);
  const grouped = GROUPS.map((g) => ({ group: g, segs: segments.filter((s) => classifyLabel(s.label) === g.id) })).filter((x) => x.segs.length > 0);
  const notes = segments.filter((s) => classifyLabel(s.label) === "notes");

  return (
    <div data-testid="briefing-view">
      {editable && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setEditing(true)} data-testid="briefing-edit-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold"><Icons.PencilLine className="h-3.5 w-3.5" />Edit summary</button>
        </div>
      )}
      <QuickFacts clientInfo={clientInfo} confidence={confidence} complexity={complexity} summaryText={value} />

      {!value?.trim() ? (
        <div className="text-sm text-slate-400 italic" data-testid="briefing-empty">No case summary yet.</div>
      ) : segments.length === 0 ? (
        // Fallback: no recognizable labels — render as readable paragraphs.
        <div className="space-y-3" data-testid="briefing-plain">
          {value.split(/\n{2,}|\r?\n/).map((p, i) => p.trim() && <p key={i} className="text-sm text-slate-700 leading-relaxed">{renderInline(p.trim())}</p>)}
        </div>
      ) : (
        <div data-testid="briefing-sections">
          {grouped.map(({ group, segs }) => <GroupBlock key={group.id} group={group} segs={segs} />)}
          {notes.length > 0 && <GroupBlock group={{ id: "notes", title: "Additional Notes", icon: "StickyNote" }} segs={notes} />}
        </div>
      )}
    </div>
  );
}

export default AllisonBriefing;
